import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
	IConfigByCountry,
	IAssistantConfig,
	IMessage,
	ThreadRolesEnum,
} from 'src/lib/schemas';
import { getAssistantConfigEventTools } from './config/distilleryAssistantConfig';
import { FunctionName, fns } from 'src/realtime/tools/distillery.tools';
import config from 'src/config';
import { getAssistantConfigByAlias } from 'src/services/assistant';
import { useAuth } from './useAuth';
import {
	safeLog,
	safeWarn,
	safeError,
	reportSentryError,
} from '../services/logging';

const baseURL = config.app.apiURL.split('/v1')[0];

interface ConversationEvent {
	type: string;
	delta?: string;
	event_id?: string;
	item?: {
		type?: string;
		role?: string;
		content?: Array<{ type: string; text: string }>;
		call_id?: string;
		output?: string;
	};
	text?: string;
	[key: string]: any;
}

interface FunctionCallMessage {
	type: 'response.function_call_arguments.done';
	name: FunctionName;
	call_id: string;
	arguments: string;
}

export default function useRealtimeChat(
	assistantAlias: string,
	initialMessage: string,
	countryCode: string,
) {
	const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
	const dataChannelRef = useRef<RTCDataChannel | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [assistantConfig, setAssistantConfig] =
		useState<IAssistantConfig | null>(null);
	const [messages, setMessages] = useState<IMessage[]>([]);
	const [isConnected, setIsConnected] = useState(false);
	const [isMyAssistantWritting, setIsMyAssistantWritting] = useState(true);
	const [isWaitingForMessage, setIsWaitingForMessage] = useState(false);
	const [countryConfig, setCountryConfig] = useState<IConfigByCountry | null>(
		null,
	);
	const connectionSetupRef = useRef<boolean>(false);
	const isAuthenticated = useAuth();

	useEffect(() => {
		const fetchAssistantConfig = async () => {
			try {
				const config = await getAssistantConfigByAlias(assistantAlias);
				setAssistantConfig(config);
				const countryConfig =
					config?.initial_prompts_by_country?.find(
						(country) => country.code === countryCode,
					) || null;
				setCountryConfig(countryConfig);
			} catch (error) {
				console.error('error fetching assistant config', error);
			}
		};
		isAuthenticated && fetchAssistantConfig();
	}, [assistantAlias, isAuthenticated]);

	function setupConnection() {
		const pc = new RTCPeerConnection();
		peerConnectionRef.current = pc;

		// skip audio
		pc.addTransceiver('audio', { direction: 'inactive' });

		const dataChannel = pc.createDataChannel('textChannel');
		dataChannelRef.current = dataChannel;

		dataChannel.onopen = () => {
			safeLog('[DataChannel] Opened');
			if (assistantConfig?.tools) {
				const event = getAssistantConfigEventTools(assistantConfig.tools);
				dataChannel.send(JSON.stringify(event));
			}
			setIsConnected(true);
			setIsLoading(false);
			sendMessage(initialMessage ?? 'Hello', 'user', true);
		};

		dataChannel.onclose = () => {
			safeLog('[DataChannel] Closed');
			setIsConnected(false);
			setIsLoading(false);
			connectionSetupRef.current = false;
		};

		dataChannel.onmessage = (event) => {
			try {
				const msg: ConversationEvent = JSON.parse(event.data);
				Sentry.addBreadcrumb({
					category: 'dataChannel',
					message: 'Received message',
					level: 'info',
					data: {
						type: msg.type,
						delta: msg.delta,
						event_id: msg.event_id,
						item: msg.item
							? {
									type: msg.item.type,
									role: msg.item.role,
									content: msg.item.content,
									call_id: msg.item.call_id,
									output: msg.item.output,
							  }
							: undefined,
					},
				});
				setIsLoading(false);

				switch (msg.type) {
					case 'response.text.delta':
						if (msg.delta) {
							setIsWaitingForMessage(false);
							Sentry.addBreadcrumb({
								category: 'dataChannel',
								message: 'Received text delta',
								level: 'info',
								data: {
									delta: msg.delta,
									event_id: msg.event_id,
								},
							});
							// Enhanced logic to handle message splitting
							setMessages((prev) => {
								const last = prev[prev.length - 1];

								// Custom components that should not be split
								const customComponents = [
									'bordered-container',
									'link-button',
									'catalog-preview',
								];

								// Check if we're inside any HTML/JSX tag or custom component
								const isInsideTag =
									last?.raw_message &&
									(() => {
										const message = last.raw_message;

										// Check for basic HTML tags
										const basicTagCheck =
											message.includes('<') &&
											!message.includes('>') &&
											message.split('<').length > message.split('>').length;

										if (basicTagCheck) return true;

										// Check for custom components - look for unclosed custom component tags
										for (const component of customComponents) {
											const openTag = `<${component}`;

											// Count opening tags
											const openCount = (
												message.match(
													new RegExp(`<${component}[^>]*(?<!/)>`, 'g'),
												) || []
											).length;
											// Count closing tags
											const closeCount = (
												message.match(new RegExp(`</${component}>`, 'g')) || []
											).length;

											// If we have more opening tags than closing tags (excluding self-closing), we're inside
											if (openCount > closeCount) {
												return true;
											}

											// Also check if we have an incomplete opening tag
											const lastOpenIndex = message.lastIndexOf(openTag);
											if (lastOpenIndex !== -1) {
												const afterOpen = message.substring(lastOpenIndex);
												// If we haven't closed this tag yet
												if (!afterOpen.includes('>')) {
													return true;
												}
											}
										}

										return false;
									})();

								// Check if there are unclosed quotes
								const hasUnclosedQuotes =
									last?.raw_message &&
									(() => {
										const message = last.raw_message;
										// Count double quotes
										const doubleQuoteCount = (message.match(/"/g) || []).length;
										// Count single quotes (but exclude apostrophes in contractions)
										const singleQuoteCount = (message.match(/'/g) || []).length;

										// If odd number of quotes, we have unclosed quotes
										return (
											doubleQuoteCount % 2 !== 0 || singleQuoteCount % 2 !== 0
										);
									})();

								const shouldCreateNewMessage =
									last?.role === ThreadRolesEnum.Assistant &&
									last.streaming &&
									!isInsideTag && // Don't create new message if inside a tag or custom component
									!hasUnclosedQuotes && // Don't create new message if there are unclosed quotes
									msg.delta?.match(/^[A-Z]/) &&
									(last.raw_message?.trim().match(/[.!?]$/) ||
										last.raw_message
											?.trim()
											.match(
												/[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF]$/,
											));

								if (
									last?.role === ThreadRolesEnum.Assistant &&
									last.streaming &&
									!shouldCreateNewMessage
								) {
									setIsMyAssistantWritting(false);
									return [
										...prev.slice(0, -1),
										{
											...last,
											raw_message: (last.raw_message || '') + msg.delta,
										},
									];
								}

								// Create a new message
								setIsMyAssistantWritting(false);
								return [
									...prev,
									{
										role: ThreadRolesEnum.Assistant,
										message_id: crypto.randomUUID(),
										raw_message: msg.delta,
										streaming: true,
										error: false,
									},
								];
							});
						}
						break;
					case 'conversation.item.created': {
						const item = msg.item;
						if (!item) return;

						if (item.type === 'function_call_output') {
							Sentry.addBreadcrumb({
								category: 'dataChannel',
								message: 'Received function call output',
								level: 'info',
								data: {
									output: item.output,
									call_id: item.call_id,
								},
							});
							safeLog(
								'[function_call_output] received toll output:',
								item.output,
							);

							createResponseEvent(dataChannelRef.current);
						} else if (
							item.role === 'assistant' &&
							item.content &&
							item.content.length > 0
						) {
							setIsWaitingForMessage(false);
							setIsMyAssistantWritting(false);
							const role = ThreadRolesEnum.Assistant;

							Sentry.addBreadcrumb({
								category: 'dataChannel',
								message: 'Received assistant message',
								level: 'info',
								data: {
									role: item.role,
									content: item.content,
									event_id: msg.event_id,
								},
							});

							const newMessage: IMessage = {
								role,
								message_id: msg.event_id ?? crypto.randomUUID(),
								raw_message: '',
								splitted_content: undefined,
								error: false,
							};

							if (item.content && item.content[0]?.text) {
								newMessage.raw_message = item.content[0].text;
							}

							setMessages((prev) => [...prev, newMessage]);
						}
						break;
					}
					case 'response.function_call_arguments.done': {
						const fCall = msg as FunctionCallMessage;
						Sentry.addBreadcrumb({
							category: 'dataChannel',
							message: 'Received function call arguments',
							level: 'info',
							data: {
								name: fCall.name,
								call_id: fCall.call_id,
								arguments: fCall.arguments,
							},
						});
						safeLog('function_call_arguments.done', msg);
						handleFunctionCall(fCall);
						break;
					}
					default:
						Sentry.addBreadcrumb({
							category: 'dataChannel',
							message: 'Received unhandled message type',
							level: 'warning',
							data: {
								type: msg.type,
								event_id: msg.event_id,
							},
						});
						safeWarn('unhandled message', msg.type, msg);
				}
			} catch (error) {
				safeError('[DataChannel] Error parsing message:', error);
				reportSentryError('[DataChannel] Error parsing message', error);
				setIsMyAssistantWritting(false);
				setIsWaitingForMessage(false);
			}
		};

		pc.createOffer()
			.then((offer) => pc.setLocalDescription(offer))
			.then(() => {
				if (!pc.localDescription) return;

				fetch(`${baseURL}/v2/rtc-connect?assistantAlias=${assistantAlias}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/sdp' },
					body: pc.localDescription.sdp,
				})
					.then((res) => res.text())
					.then((answerSDP) => {
						if (!answerSDP.startsWith('v=')) {
							safeWarn('[DataChannel] Invalid SDP Answer:', answerSDP);
							setIsLoading(false);
							connectionSetupRef.current = false;
							return;
						}
						pc.setRemoteDescription({
							type: 'answer',
							sdp: answerSDP,
						});
					})
					.catch((err) => {
						safeError('[DataChannel] Error:', err);
						setIsLoading(false);
						connectionSetupRef.current = false;
					});
			})
			.catch((err) => {
				safeError('[DataChannel] Error creating offer:', err);
				setIsLoading(false);
				connectionSetupRef.current = false;
			});

		return pc;
	}

	useEffect(() => {
		if (!assistantConfig || connectionSetupRef.current) {
			return;
		}

		const pc = setupConnection();
		connectionSetupRef.current = true;

		return () => {
			pc.close();
			connectionSetupRef.current = false;
		};
	}, [assistantConfig]);

	function startNewConversation() {
		setMessages([]);
		setIsMyAssistantWritting(true);
		setIsConnected(false);
		connectionSetupRef.current = false;
		if (peerConnectionRef.current) {
			peerConnectionRef.current.close();
			peerConnectionRef.current = null;
		}
		dataChannelRef.current = null;

		setupConnection();
	}

	function createResponseEvent(dataChannel: any) {
		const createResponseEvent = {
			type: 'response.create',
			response: {
				modalities: ['text'],
			},
		};

		dataChannel.send(JSON.stringify(createResponseEvent));
	}

	function sendMessage(
		text: string,
		role: string = 'user',
		hidden: boolean = false,
	) {
		const dc = dataChannelRef.current;
		if (!dc || dc.readyState !== 'open') {
			safeWarn('[DataChannel] Not open');
			reportSentryError('[DataChannel] Not open', {
				level: 'warning',
				extra: {
					readyState: dc?.readyState,
					text,
					role,
					hidden,
				},
			});
			return;
		}

		setIsWaitingForMessage(true);

		const messageEvent = {
			event_id: `event-${Date.now()}`,
			type: 'conversation.item.create',
			item: {
				type: 'message',
				role: role,
				content: [{ type: 'input_text', text }],
			},
		};

		try {
			dc.send(JSON.stringify(messageEvent));
			Sentry.addBreadcrumb({
				category: 'dataChannel',
				message: 'Sent message',
				level: 'info',
				data: {
					event_id: messageEvent.event_id,
					type: messageEvent.type,
					item: {
						type: messageEvent.item.type,
						role: messageEvent.item.role,
						content: messageEvent.item.content,
					},
				},
			});

			// Add additional breadcrumb for the actual message content
			Sentry.addBreadcrumb({
				category: 'dataChannel',
				message: 'Message content',
				level: 'info',
				data: {
					text,
					role,
					hidden,
				},
			});
		} catch (error) {
			reportSentryError('[DataChannel] Error sending message', error);
			throw error;
		}

		if (!hidden) {
			const userMsg: IMessage = {
				role: role,
				raw_message: text,
				message_id: crypto.randomUUID(),
			};
			setMessages((prev) => [...prev, userMsg]);
		} else {
			setMessages([]);
		}

		createResponseEvent(dc);
	}

	async function handleFunctionCall(
		msg: FunctionCallMessage,
	) {
		safeLog('[FunctionCall] Received call:', msg.name);
		setIsMyAssistantWritting(true);
		const dc = dataChannelRef.current;
		if (!dc) {
			setIsMyAssistantWritting(false);
			return;
		}

		const fn = fns[msg.name];
		if (fn !== undefined) {
			safeLog(`Calling local function ${msg.name} with ${msg.arguments}`);

			// Handle JSON parsing with error handler for OpenAI API bug
			let args;
			try {
				args = JSON.parse(msg.arguments);
			} catch (parseError: any) {
				safeError(
					`[JSON Parse Error] Invalid JSON from OpenAI API for function ${msg.name}:`,
					parseError,
				);
				safeError(`[JSON Parse Error] Raw arguments:`, msg.arguments);

				// Send error response and reset state to prevent hanging
				const event = {
					type: 'conversation.item.create',
					item: {
						type: 'function_call_output',
						call_id: msg.call_id,
						output: JSON.stringify({
							success: false,
							message: `Unable to parse function arguments due to malformed JSON from API. Please try again.`,
						}),
					},
				};
				dc.send(JSON.stringify(event));
				setIsMyAssistantWritting(false);
				return;
			}

			const populatedArgs = populateArgumentsWithConfigs(msg.name, args);

			try {
				const result = await fn(populatedArgs);
				safeLog('Function result:', result);

				const event = {
					type: 'conversation.item.create',
					item: {
						type: 'function_call_output',
						call_id: msg.call_id,
						output: JSON.stringify(result),
					},
				};
				dc.send(JSON.stringify(event));
			} catch (e) {
				safeError(`Error calling function ${msg.name}`, e);
				// Let the system know that the function has been called and share its output
				const event = {
					type: 'conversation.item.create',
					item: {
						type: 'function_call_output',
						call_id: msg.call_id,
						output: JSON.stringify({
							success: false,
							message: `The function ${msg.name} failed for args ${populatedArgs}`,
						}),
					},
				};
				dc.send(JSON.stringify(event));
			}
		} else {
			setIsMyAssistantWritting(false);
		}
	}

	// Helper functions for populateArgumentsWithConfigs
	function isQuestAssistant(): boolean {
		return assistantAlias === 'distillery-quest';
	}

	function configureEmailArgs(args: any): void {
		args.assistant_alias = assistantAlias;
		if (countryConfig && countryConfig.email_to) {
			args.email_to = countryConfig.email_to;
		}
	}

	function getTemplatesForReviewCard(withPicture: boolean, isAiCard: boolean = false): {
		square: string;
		horizontal: string;
	} {
		if (isQuestAssistant()) {
			return withPicture
				? {
						square: 'quest-5a-photo-square',
						horizontal: 'quest-5b-photo-horizontal',
				  }
				: {
						square: 'quest-1a-no-photo-square',
						horizontal: 'quest-1b-no-photo-horizontal',
				  };
		}

		if (isAiCard) {
			return withPicture
				? {
						square: 'distillery-gcloud-v2-picture-ai-square',
						horizontal: 'distillery-gcloud-v2-picture-ai-horizontal',
				  }
				: {
						square: 'distillery-google-next-square',
						horizontal: 'distillery-google-next-horizontal',
				  };
		}

		return withPicture
			? {
					square: 'distillery-gcloud-v2-picture-no-ai-square',
					horizontal: 'distillery-gcloud-v2-picture-no-ai-horizontal',
			  }
			: {
					square: 'distillery-gcloud-v2-no-picture-no-ai-square',
					horizontal: 'distillery-gcloud-v2-no-picture-no-ai-horizontal',
			  };
	}

	function configureReviewCardWithPicture(args: any, isAiCard: boolean = false): void {
		if (!assistantConfig?.app_url_with_picture) return;

		args.app_url = assistantConfig.app_url_with_picture;
		const templates = getTemplatesForReviewCard(true, isAiCard);
		args.template_id_square = templates.square;
		args.template_id_horizontal = templates.horizontal;
	}

	function configureReviewCardWithoutPicture(args: any, isAiCard: boolean = false): void {
		if (!assistantConfig?.app_url_without_picture) return;

		args.app_url = assistantConfig.app_url_without_picture;
		const templates = getTemplatesForReviewCard(false, isAiCard);
		args.template_id_square = templates.square;
		args.template_id_horizontal = templates.horizontal;
	}

	function populateArgumentsWithConfigs(functionName: string, args: any) {
		switch (functionName) {
			case 'send_review_card_email':
				configureEmailArgs(args);
				break;

			case 'generate_review_card':
				if (assistantConfig) {
					configureReviewCardWithPicture(args);
				}
				break;

			case 'generate_ai_card_with_profile_picture':
				if (assistantConfig) {
					configureReviewCardWithPicture(args, true);
				}
				break;

			case 'generate_review_card_without_profile_picture':
				if (assistantConfig) {
					configureReviewCardWithoutPicture(args);
				}
				break;

			case 'generate_ai_card_without_profile_picture':
				if (assistantConfig) {
					configureReviewCardWithoutPicture(args, true);
				}
				break;
		}

		return args;
	}

	return {
		isConnected,
		isMyAssistantWritting,
		messages,
		sendMessage,
		countryConfig,
		isLoading,
		setIsLoading,
		startNewConversation,
		isWaitingForMessage,
	};
}
